import { Box, Grid, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Loader";
import BoxCheckInput from "../../../components/BoxCheckInput";
import { useFormik } from "formik";
import * as yup from "yup";
import { BOOKING_AS, getKycOptions } from "../../../config/constant";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import {
  fetchDigiLockerURL,
  getImageDetailsOCR,
  setActiveStep,
  setKycVerificationActiveStep,
} from "../new-booking-form.slice";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NoDigilockerAccountFields from "./NoDigilockerAccountFields";
import { getLastStep, getErrorMessage } from "../../../utilis/common-funtions";
import { getApplicantDetails } from "../new-booking-form.slice";
import { route } from "../../../services/apiRoutes";
import CircleIcon from "@mui/icons-material/Circle";

const AuthorizedSignatory = ({
  applicantNo,
  residentStatus = "",
  relationship = null,
  individualErrors = {},
  isIndividualFlow = false,
}) => {
  const {
    isLoading,
    applicantData,
    activeStep,
    kycVerificationActiveStep,
    applicantTotal,
  } = useSelector((state) => state?.newBookingForm);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const KYC_OPTIONS = getKycOptions(t);
  const { oppId } = useParams();
  const { isCorporateFlow } = useSelector((state) => state?.newBookingForm);
  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const applicantKey = `applicant${applicantNo}`;
  const currentApplicant = applicantData?.[applicantKey];
  const personalDetails = currentApplicant?.personalDetails;
  const hasAadhaarName = Boolean(personalDetails?.isAadhaarVerified);
  const hasPanName = Boolean(personalDetails?.isPanVerified);
  const showCheckBoxDigilockerOption = !personalDetails;
  const isOCIFlow =
    isIndividualFlow && residentStatus === t("residentStatusOptions.PIO_OCI");

  const authorizedSignatoryFormik = useFormik({
    initialValues: {
      kycOptions: "",
      aadhaarImage: [],
      panImage: [],
      residentStatus: "",
      passportImage: [],
      passportHaveCurrentAddress: true,
      ociCardImage: [],
      isPhysicalAadhaar: false,
      isPhysicalPan: false,
      addressProofType: "",
      addressProofImage: [],
      isAadhaarSkipped: false,
      isPanSkipped: false,
      isPhysicalPassport: false,
      isPhysicalOCI: false,
      isPhysicalAddressProof: false,
    },
    validationSchema: yup.object().shape({
      kycOptions: yup
        .string()
        .required(
          t("authorizedSignatoryDetails.fields.kycOption.validation.required")
        ),
      aadhaarImage: yup
        .array()
        .when(["isAadhaarSkipped", "kycOptions", "residentStatus"], {
          is: (isAadhaarSkipped, kycOptions, residentStatus) =>
            !isAadhaarSkipped &&
            kycOptions === t("kycOptions.1.value") &&
            !hasAadhaarName &&
            residentStatus !== t("residentStatusOptions.PIO_OCI"),
          then: () =>
            yup
              .array()
              .min(
                1,
                t(
                  "authorizedSignatoryDetails.fields.aadhaarImage.validation.min"
                )
              )
              .required(
                t(
                  "authorizedSignatoryDetails.fields.aadhaarImage.validation.required"
                )
              ),
          otherwise: () => yup.array().notRequired(),
        }),

      panImage: yup.array().when(["isPanSkipped", "kycOptions"], {
        is: (isPanSkipped, kycOptions) =>
          !isPanSkipped &&
          kycOptions === t("kycOptions.1.value") &&
          !hasPanName,
        then: () =>
          yup
            .array()
            .min(
              1,
              t(
                "authorizedSignatoryDetails.fields.panNumberImage.validation.min"
              )
            )
            .required(
              t(
                "authorizedSignatoryDetails.fields.panNumberImage.validation.required"
              )
            ),
        otherwise: () => yup.array().notRequired(),
      }),

      addressProofImage: yup
        .array()
        .when(
          [
            "isAadhaarSkipped",
            "residentStatus",
            "passportHaveCurrentAddress",
            "isPhysicalAddressProof",
          ],
          {
            is: (
              isAadhaarSkipped,
              residentStatus,
              passportHaveCurrentAddress,
              isPhysicalAddressProof
            ) =>
              !isPhysicalAddressProof &&
              ([
                t("residentStatusOptions.NRI"),
                t("residentStatusOptions.PIO_OCI"),
              ].includes(residentStatus)
                ? !passportHaveCurrentAddress
                : isAadhaarSkipped),
            then: () =>
              yup
                .array()
                .min(
                  1,
                  t(
                    "authorizedSignatoryDetails.fields.addressProofImage.validation.min"
                  )
                )
                .required(
                  t(
                    "authorizedSignatoryDetails.fields.addressProofImage.validation.required"
                  )
                ),
            otherwise: () => yup.array().notRequired(),
          }
        ),

      addressProofType: yup
        .string()
        .when(
          [
            "isAadhaarSkipped",
            "residentStatus",
            "passportHaveCurrentAddress",
            "isPhysicalAddressProof",
          ],
          {
            is: (
              isAadhaarSkipped,
              residentStatus,
              passportHaveCurrentAddress,
              isPhysicalAddressProof
            ) =>
              !isPhysicalAddressProof &&
              ([
                t("residentStatusOptions.NRI"),
                t("residentStatusOptions.PIO_OCI"),
              ].includes(residentStatus)
                ? !passportHaveCurrentAddress
                : isAadhaarSkipped),
            then: (schema) =>
              schema.required(
                t(
                  "authorizedSignatoryDetails.fields.addressProofImage.validation.select"
                )
              ),
            otherwise: (schema) => schema.notRequired(),
          }
        ),

      passportImage: yup
        .array()
        .when(["residentStatus", "kycOptions", "isPhysicalPassport"], {
          is: (residentStatus, kycOptions, isPhysicalPassport) =>
            !isPhysicalPassport &&
            [
              t("residentStatusOptions.NRI"),
              t("residentStatusOptions.PIO_OCI"),
            ].includes(residentStatus) &&
            kycOptions === t("kycOptions.1.value"),
          then: () =>
            yup
              .array()
              .min(
                1,
                t(
                  "authorizedSignatoryDetails.fields.passportImage.validation.min"
                )
              )
              .required(
                t(
                  "authorizedSignatoryDetails.fields.passportImage.validation.required"
                )
              ),
          otherwise: () => yup.array().notRequired(),
        }),

      ociCardImage: yup.array().when(["residentStatus", "isPhysicalOCI"], {
        is: (residentStatus, isPhysicalOCI) =>
          !isPhysicalOCI &&
          residentStatus === t("residentStatusOptions.PIO_OCI"),
        then: () =>
          yup
            .array()
            .min(
              1,
              t("authorizedSignatoryDetails.fields.ociCardImage.validation.min")
            )
            .required(
              t(
                "authorizedSignatoryDetails.fields.ociCardImage.validation.required"
              )
            ),
        otherwise: () => yup.array().notRequired(),
      }),
    }),

    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    authorizedSignatoryFormik.resetForm();
    authorizedSignatoryFormik.setFieldValue("residentStatus", residentStatus);
    if (residentStatus === t("residentStatusOptions.PIO_OCI")) {
      authorizedSignatoryFormik.setFieldValue(
        "kycOptions",
        t("kycOptions.1.value")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentStatus]);

  useEffect(() => {
    if (applicantData?.[`applicant${applicantNo}`]) {
      authorizedSignatoryFormik.setFieldValue(
        "kycOptions",
        t("kycOptions.1.value")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo, dispatch]);

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleOnChange = (e, index, key, list) => {
    let selectedValue = list[index]?.value;
    authorizedSignatoryFormik.setFieldValue(key, selectedValue);
  };

  const handleSaveForm = async () => {
    const errors = await authorizedSignatoryFormik.validateForm();

    if (Object.keys(errors).length > 0) {
      authorizedSignatoryFormik.setTouched(
        Object.keys(authorizedSignatoryFormik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      return;
    }

    if (isIndividualFlow && Object.keys(individualErrors).length > 0) {
      return;
    }

    if (
      authorizedSignatoryFormik?.values?.kycOptions?.toLowerCase() ===
      t("kycOptions.0.value")?.toLowerCase()
    ) {
      try {
        const payload = {
          // redirectUrl: (() => {
          //   const isLocalhost =
          //     window.location.hostname === "localhost" ||
          //     window.location.hostname === "127.0.0.1";

          //   const baseUrl = isLocalhost
          //     ? process.env.REACT_APP_NGROK_URL
          //     : process.env.REACT_BOOKING_FORM_URL;

          //   return baseUrl.replace(/\/+$/, "") + window.location.pathname;
          // })(),
          redirectUrl: window.location.href,

          lastStep: applicantTotal - 1,
          applicantNumber: applicantNo,
          opportunityId: oppId,
          ...(isIndividualFlow
            ? { residentStatus: residentStatus, relationship: relationship }
            : {}),
          bookingAs: isIndividualFlow
            ? BOOKING_AS["INDIVIDUAL"]
            : isCorporateFlow
            ? BOOKING_AS["CORPORATE"]
            : BOOKING_AS["PARTNERSHIP_FIRM"],
        };
        const response = await dispatch(fetchDigiLockerURL(payload)).unwrap();
        if (response) {
          console.log("Second User URl", response);
        }
        window.location.href = response?.session_url;
      } catch (error) {
        setSnackBarconfig({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
      return;
    }

    try {
      const payload = buildPayload(false);
      const res = await dispatch(getImageDetailsOCR(payload)).unwrap();
      dispatch(setKycVerificationActiveStep(kycVerificationActiveStep + 1));
      if (res) {
        dispatch(getApplicantDetails(`/${oppId}`));
      }
    } catch (err) {
      setSnackBarconfig({
        open: true,
        message: getErrorMessage(err),
        severity: "error",
      });
    }
  };

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };
  const buildPayload = (saveForLater = false) => {
    const payload = {
      ...(authorizedSignatoryFormik?.values?.aadhaarImage?.length
        ? { aadhaarImage: authorizedSignatoryFormik.values.aadhaarImage }
        : {}),
      ...(authorizedSignatoryFormik?.values?.panImage?.length
        ? { panImage: authorizedSignatoryFormik.values.panImage }
        : {}),
      lastStep: applicantTotal - 1,
      applicantNumber: applicantNo,
      opportunityId: oppId,
      bookingAs: applicantData?.bookingAs,
      isPanSkipped: authorizedSignatoryFormik?.values?.isPanSkipped,
      ...(!hasAadhaarName
        ? {
            isAadhaarSkipped:
              authorizedSignatoryFormik?.values?.isAadhaarSkipped,
          }
        : {}),
      ...(authorizedSignatoryFormik?.values?.addressProofImage?.length
        ? {
            addressProofImage:
              authorizedSignatoryFormik.values.addressProofImage,
          }
        : {}),
      ...(authorizedSignatoryFormik?.values?.addressProofType
        ? {
            addressProofType: authorizedSignatoryFormik.values.addressProofType,
          }
        : {}),
      residentStatus: authorizedSignatoryFormik?.values?.residentStatus,
      relationship: relationship,
      ...(authorizedSignatoryFormik?.values?.passportImage
        ? {
            passportImage: authorizedSignatoryFormik?.values?.passportImage,
          }
        : {}),
      ...(authorizedSignatoryFormik?.values?.ociCardImage
        ? {
            ociCardImage: authorizedSignatoryFormik?.values?.ociCardImage,
          }
        : {}),
      isPhysicalPassport: authorizedSignatoryFormik?.values?.isPhysicalPassport,
      isPhysicalAddressProof:
        authorizedSignatoryFormik?.values?.isPhysicalAddressProof,
      isPhysicalOCI: authorizedSignatoryFormik?.values?.isPhysicalOCI,

      passportHaveCurrentAddress:
        authorizedSignatoryFormik?.values?.passportHaveCurrentAddress,
    };

    return {
      opportunityId: oppId,
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      saveForLater,
      ...payload,
    };
  };
  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      {(!isIndividualFlow || (isIndividualFlow && residentStatus)) && (
        <div>
          {!showCheckBoxDigilockerOption && (
            <Box
              sx={{
                border: "1px solid rgba(218, 218, 218, 1)",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "24px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#1A407D",
                  fontWeight: "600",
                  lineHeight: "36px",
                  mb: 1,
                }}
              >
                {t("missingDocuments.title")}
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#1A407D",
                  fontWeight: "500",
                }}
              >
                {t("missingDocuments.subTitle1")}
                <br />
                {t("missingDocuments.subTitle2")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#1A407D",
                  fontWeight: "500",
                  mb: 2,
                }}
              >
                {hasAadhaarName && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "7px" }}
                  >
                    <CircleIcon sx={{ fontSize: "6px" }} />
                    {t("missingDocuments.aadhaar")}
                  </Box>
                )}
                {hasAadhaarName && hasPanName && <br />}
                {hasPanName && t("missingDocuments.pan")}
              </Typography>
            </Box>
          )}

          {!showCheckBoxDigilockerOption && (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#1C252E",
                fontWeight: "700",
                mb: 1,
              }}
            >
              {t("missingDocuments.uploadPrompt")}
            </Typography>
          )}
          <Grid container spacing={2}>
            {showCheckBoxDigilockerOption && (
              <Grid item xs={12}>
                <Box
                  component="ul"
                  sx={{
                    border: "1px solid #DADADA",
                    borderRadius: "8px",
                    padding: "16px",
                    paddingInlineStart: "40px",
                  }}
                >
                  {!isOCIFlow &&
                    t("digiLockerInfoUiText", { returnObjects: true })?.map(
                      (text, index) => (
                        <Typography
                          key={index}
                          component="li"
                          variant="body2"
                          sx={{
                            listStyleType: "disc",
                            color: "#1A407D",
                            fontWeight: 600,
                            textAlign: "justify",
                            my: 1,
                          }}
                        >
                          {text}
                        </Typography>
                      )
                    )}
                  {residentStatus === t("residentStatusOptions.NRI") && (
                    <Typography
                      component="li"
                      variant="body2"
                      sx={{
                        listStyleType: "disc",
                        color: "#1A407D",
                        fontWeight: 600,
                        textAlign: "justify",
                        my: 1,
                      }}
                    >
                      {t("digilockerInfoPassportText")}
                    </Typography>
                  )}
                  {residentStatus === t("residentStatusOptions.PIO_OCI") && (
                    <Typography
                      component="li"
                      variant="body2"
                      sx={{
                        listStyleType: "disc",
                        color: "#1A407D",
                        fontWeight: 600,
                        textAlign: "justify",
                        my: 1,
                      }}
                    >
                      {t("digilockerInfoOCICardText")}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {showCheckBoxDigilockerOption && !isOCIFlow && (
              <Grid item xs={12}>
                <BoxCheckInput
                  field={{
                    name: "kycOptions",
                    required: false,
                    readonly: false,
                    options: KYC_OPTIONS,
                    grid: true,
                    xsCol: 12,
                    smCol: 6,
                    mdCol: 6,
                    lgCol: 6,
                    mb: 0,
                    handleOnChange,
                  }}
                  formik={authorizedSignatoryFormik}
                />
              </Grid>
            )}

            {authorizedSignatoryFormik.values.kycOptions ===
              t("kycOptions.1.value") && (
              <NoDigilockerAccountFields
                formik={authorizedSignatoryFormik}
                applicantNo={applicantNo}
              />
            )}
          </Grid>
        </div>
      )}
      <CommonButton
        handleNext={handleSaveForm}
        handlePrevious={handlePrevious}
        isSaveAsDraftDisabled={
          authorizedSignatoryFormik?.values?.kycOptions?.toLowerCase() !==
          t("kycOptions.1.value")?.toLowerCase()
        }
        saveDraftData={{
          ...buildPayload(true),
        }}
        saveDraftMethod={"POST"}
        saveDraftPath={`${route.GET_IMAGE_DETAILS_OCR}`}
        saveForLater={false}
        showPrevBtn={
          applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL && activeStep === 0
            ? true
            : false
        }
      />
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default memo(AuthorizedSignatory);
